import {
  createContext,
  useContext,
  useReducer,
  useRef,
  useMemo,
  type ReactNode,
} from 'react';
import type { ToolName } from '../types';
import type { ToolOptions } from '../lib/tool-groups';
import { DEFAULT_TOOL_OPTIONS } from '../lib/tool-groups';

/** Tool state managed by the ToolContext reducer. */
export interface ToolState {
  /** Currently active tool */
  active: ToolName;
  /** Previous tool (for hold-to-switch revert). Null when in toggle mode. */
  previous: ToolName | null;
  /** Typed tool options (persisted via useToolOptions) */
  options: ToolOptions;
  /** When true, `tool:set` clears previous (toggle mode). When false, saves previous (hold-to-switch). */
  locked: boolean;
}

/** Actions dispatched to the tool reducer. */
export type ToolAction =
  | { type: 'tool:set'; tool: ToolName }
  | { type: 'tool:revert' }
  | { type: 'tool:setOption'; key: string; value: unknown }
  | { type: 'tool:setOptions'; options: Partial<ToolOptions> }
  | { type: 'tool:lock'; locked: boolean };

/** Value provided by ToolContext. */
export interface ToolContextValue {
  /** Current tool state */
  state: ToolState;
  /** Raw reducer dispatch */
  dispatch: React.Dispatch<ToolAction>;
  /** Convenience: dispatch({ type: 'tool:set', tool }) */
  setTool: (tool: ToolName) => void;
  /** Ref synced with state — use in canvas render callbacks to avoid re-renders */
  stateRef: React.RefObject<ToolState>;
}

/** Initial tool state: select tool, locked (toggle mode). */
export const INITIAL_TOOL_STATE: ToolState = {
  active: 'select',
  previous: null,
  options: { ...DEFAULT_TOOL_OPTIONS },
  locked: true,
};

/** Reducer for tool state transitions. */
export function toolReducer(state: ToolState, action: ToolAction): ToolState {
  switch (action.type) {
    case 'tool:set': {
      if (state.locked) {
        // Toggle mode — no previous saved
        return { ...state, active: action.tool, previous: null };
      }
      // Hold-to-switch mode — save current as previous
      return { ...state, active: action.tool, previous: state.active };
    }
    case 'tool:revert': {
      if (state.previous === null) return state;
      return { ...state, active: state.previous, previous: null };
    }
    case 'tool:setOption': {
      return {
        ...state,
        options: { ...state.options, [action.key]: action.value } as ToolOptions,
      };
    }
    case 'tool:setOptions': {
      return {
        ...state,
        options: { ...state.options, ...action.options },
      };
    }
    case 'tool:lock': {
      return { ...state, locked: action.locked };
    }
    default:
      return state;
  }
}

const ToolContext = createContext<ToolContextValue | null>(null);

/** Provider for tool state. Place above CanvasProvider in the tree. */
export function ToolProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(toolReducer, INITIAL_TOOL_STATE);
  const stateRef = useRef(state);
  stateRef.current = state;

  const value = useMemo<ToolContextValue>(() => ({
    state,
    dispatch,
    setTool: (tool: ToolName) => dispatch({ type: 'tool:set', tool }),
    stateRef,
  }), [state, dispatch]);

  return (
    <ToolContext.Provider value={value}>{children}</ToolContext.Provider>
  );
}

/**
 * Access ToolContext value. Must be used within a ToolProvider.
 * @throws Error if used outside ToolProvider.
 */
export function useToolContext(): ToolContextValue {
  const ctx = useContext(ToolContext);
  if (!ctx) throw new Error('useToolContext must be used within ToolProvider');
  return ctx;
}
